export class SplitError extends Error {
    constructor(message) {
        super(message);
        this.name = "SplitError";
    }
}
/**
 * 重み (weight) に比例して total を整数配分する。
 * 端数は「小数部が大きい順 → memberId 昇順」で 1 単位ずつ配る。
 */
function allocateByWeight(total, weights) {
    const sum = weights.reduce((acc, w) => acc + w.weight, 0);
    if (sum <= 0)
        throw new SplitError("負担割合の合計が 0 です");
    const raw = weights.map((w) => {
        const exact = (total * w.weight) / sum;
        const base = Math.floor(exact);
        return { memberId: w.memberId, base, remainder: exact - base };
    });
    let assigned = raw.reduce((acc, r) => acc + r.base, 0);
    let leftover = total - assigned;
    const order = [...raw].sort((a, b) => {
        if (b.remainder !== a.remainder)
            return b.remainder - a.remainder;
        return a.memberId < b.memberId ? -1 : a.memberId > b.memberId ? 1 : 0;
    });
    const result = new Map(raw.map((r) => [r.memberId, r.base]));
    let i = 0;
    while (leftover > 0 && order.length > 0) {
        const target = order[i % order.length];
        result.set(target.memberId, (result.get(target.memberId) ?? 0) + 1);
        leftover -= 1;
        i += 1;
    }
    return weights.map((w) => ({ memberId: w.memberId, amount: result.get(w.memberId) ?? 0 }));
}
/** 負担ルールに従って支出額を配分する */
export function allocate(amount, rule) {
    if (!Number.isSafeInteger(amount) || amount < 0) {
        throw new SplitError("支出額は 0 以上の整数である必要があります");
    }
    switch (rule.type) {
        case "equal": {
            const ids = dedupe(rule.memberIds);
            return allocateByWeight(amount, ids.map((memberId) => ({ memberId, weight: 1 })));
        }
        case "shares": {
            assertUnique(rule.entries.map((e) => e.memberId));
            if (rule.entries.some((e) => e.share <= 0))
                throw new SplitError("比率は正の数で指定してください");
            return allocateByWeight(amount, rule.entries.map((e) => ({ memberId: e.memberId, weight: e.share })));
        }
        case "percentage": {
            assertUnique(rule.entries.map((e) => e.memberId));
            const total = rule.entries.reduce((acc, e) => acc + e.percent, 0);
            if (Math.abs(total - 100) > 0.01) {
                throw new SplitError(`パーセントの合計が 100 になっていません (現在 ${total}%)`);
            }
            return allocateByWeight(amount, rule.entries.map((e) => ({ memberId: e.memberId, weight: e.percent })));
        }
        case "exact": {
            assertUnique(rule.entries.map((e) => e.memberId));
            const total = rule.entries.reduce((acc, e) => acc + e.amount, 0);
            if (total !== amount) {
                throw new SplitError(`個別金額の合計 (${total}) が支出額 (${amount}) と一致しません`);
            }
            return rule.entries.map((e) => ({ memberId: e.memberId, amount: e.amount }));
        }
        case "adjusted": {
            assertUnique(rule.entries.map((e) => e.memberId));
            return adjustedAllocation(amount, rule.entries);
        }
    }
}
/**
 * 一部のメンバーだけ金額を固定し、残額を他のメンバーで均等割りする。
 *
 * 「お酒を飲まなかった人だけ 2,000 円」のような調整を、
 * 残りの人の負担額を自動で計算しながら実現するための中核ロジック。
 * amount が null のメンバーが残額の割り勘対象になる。
 */
export function adjustedAllocation(total, entries) {
    const fixed = entries.filter((e) => e.amount !== null);
    const flexible = entries.filter((e) => e.amount === null);
    const fixedTotal = fixed.reduce((acc, e) => acc + e.amount, 0);
    if (fixedTotal > total) {
        throw new SplitError(`固定した金額の合計 (${fixedTotal}) が支出額 (${total}) を超えています`);
    }
    if (flexible.length === 0) {
        if (fixedTotal !== total) {
            throw new SplitError(`個別金額の合計 (${fixedTotal}) が支出額 (${total}) と一致しません`);
        }
        return entries.map((e) => ({ memberId: e.memberId, amount: e.amount ?? 0 }));
    }
    const remainder = allocateByWeight(total - fixedTotal, flexible.map((e) => ({ memberId: e.memberId, weight: 1 })));
    const remainderById = new Map(remainder.map((r) => [r.memberId, r.amount]));
    // 入力の順序を保ったまま返す (UI の並びと一致させるため)
    return entries.map((e) => ({
        memberId: e.memberId,
        amount: e.amount ?? remainderById.get(e.memberId) ?? 0,
    }));
}
/**
 * 「1人あたりいくらか」のプレビュー。
 * 金額を入力した直後に、まだ調整していない状態の負担額を表示するために使う。
 */
export function previewEqualShares(total, memberIds) {
    if (memberIds.length === 0)
        return [];
    return allocate(total, { type: "equal", memberIds });
}
/** 支出そのものの整合性を検証する。問題があればエラーメッセージの配列を返す */
export function validateExpense(expense, knownMemberIds) {
    const errors = [];
    const known = new Set(knownMemberIds);
    const payerTotal = expense.payers.reduce((acc, p) => acc + p.amount, 0);
    if (payerTotal !== expense.amount) {
        errors.push(`立替金額の合計 (${payerTotal}) が支出額 (${expense.amount}) と一致しません`);
    }
    for (const p of expense.payers) {
        if (!known.has(p.memberId))
            errors.push(`立替者 ${p.memberId} がメンバーに存在しません`);
    }
    const participantIds = splitMemberIds(expense.split);
    if (participantIds.length === 0)
        errors.push("負担者が1人も指定されていません");
    for (const id of participantIds) {
        if (!known.has(id))
            errors.push(`負担者 ${id} がメンバーに存在しません`);
    }
    try {
        allocate(expense.amount, expense.split);
    }
    catch (e) {
        if (e instanceof SplitError)
            errors.push(e.message);
        else
            throw e;
    }
    return errors;
}
/** 負担ルールに登場するメンバー ID を取り出す */
export function splitMemberIds(rule) {
    switch (rule.type) {
        case "equal":
            return dedupe(rule.memberIds);
        case "shares":
        case "percentage":
        case "exact":
        case "adjusted":
            return dedupe(rule.entries.map((e) => e.memberId));
    }
}
function dedupe(ids) {
    return [...new Set(ids)];
}
function assertUnique(ids) {
    if (new Set(ids).size !== ids.length)
        throw new SplitError("同じメンバーが重複して指定されています");
}
//# sourceMappingURL=split.js.map